import React, { useState, useEffect } from "react";
import { 
  ThemeProvider, 
  CssBaseline, 
  Paper, 
  Typography, 
  Box, 
  Button, 
  TextField, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemText, 
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup
} from "@mui/material";
import { 
  CloudUpload, 
  Close, 
  Description, 
  Assignment, 
  Lightbulb, 
  AccountTree,
  InsertDriveFile,
  AssignmentTurnedIn,
  Edit,
  Save,
  Cancel,
  Visibility,
  Delete
} from "@mui/icons-material";
import theme from "./theme";
import { 
  fetchProtocols, 
  fetchDocuments,
  fetchDocument,
  fetchSuggestions,
  fetchGraphData,
  searchProtocols, 
  generateProtocol, 
  saveVersion, 
  ingestFileUpload,
  deleteDocument,
  updateDocument,
  deleteProtocol,
  updateProtocol 
} from "./api";
import KnowledgeGraph from "./components/KnowledgeGraph";
import EnhancedSearch from "./components/EnhancedSearch";
import { ORG_STRUCTURE, getDepartments, getRolesForDepartment } from "./orgStructure";

export default function App() {
  // View state
  const [activeTab, setActiveTab] = useState("knowledge"); // knowledge, protocols, suggestions, graph
  
  // Data state
  const [protocols, setProtocols] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [filteredProtocols, setFilteredProtocols] = useState([]);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  const [editDocumentTitle, setEditDocumentTitle] = useState("");
  const [editingDocumentMode, setEditingDocumentMode] = useState(false);
  const [editDocumentData, setEditDocumentData] = useState({});
  
  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState({});
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMetadata, setUploadMetadata] = useState({
    title: "",
    roles: "",
    department: ""
  });

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all data in parallel
      const [protocolsRes, documentsRes, suggestionsRes, graphRes] = await Promise.all([
        fetchProtocols(),
        fetchDocuments(), 
        fetchSuggestions(),
        fetchGraphData()
      ]);
      
      setProtocols(protocolsRes.protocols || []);
      setFilteredProtocols(protocolsRes.protocols || []);
      setDocuments(documentsRes.documents || []);
      setSuggestions(suggestionsRes.suggestions || []);
      setGraphData(graphRes);
    } catch (err) {
      setError("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (filters = {}) => {
    if (!searchQuery.trim()) {
      setFilteredProtocols(protocols);
      return;
    }

    try {
      setLoading(true);
      // Enhanced search with role/department filters
      const searchParams = new URLSearchParams();
      searchParams.append('q', searchQuery);
      if (filters.role) searchParams.append('role', filters.role);
      if (filters.department) searchParams.append('department', filters.department);
      if (filters.content_type) searchParams.append('content_type', filters.content_type);
      
      const response = await searchProtocols(searchQuery + (Object.keys(filters).length ? '&' + searchParams.toString().split('q=' + encodeURIComponent(searchQuery) + '&')[1] : ''));
      const searchResults = response.results || [];
      
      // For protocols tab, filter protocols; for knowledge tab, show all results
      if (activeTab === 'protocols') {
        const protocolResults = searchResults.filter(r => r.content_type === 'protocol' || r.id.startsWith('protocol_'));
        const resultIds = protocolResults.map(r => r.id.replace('protocol_', ''));
        const filtered = protocols.filter(p => resultIds.includes(p.id));
        setFilteredProtocols(filtered);
      }
      
      return searchResults;
    } catch (err) {
      setError("Search failed: " + err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProtocol = async () => {
    if (!searchQuery.trim()) {
      setError("Please enter a search query to generate a protocol");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // First search for relevant chunks
      const searchResponse = await searchProtocols(searchQuery);
      const chunks = searchResponse.results?.map(r => r.title) || [searchQuery];
      
      const response = await generateProtocol({
        title: `Generated: ${searchQuery}`,
        chunks: chunks,
        roles: [],
        department: null
      });
      
      if (response.status === "ok") {
        await loadAllData(); // Refresh all data
        setError(null);
        alert("Protocol generated successfully!");
      }
    } catch (err) {
      setError("Failed to generate protocol: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVersion = async () => {
    if (!selectedProtocol || !editContent.title) {
      setError("Please select a protocol and make changes before saving");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await saveVersion({
        protocol_id: selectedProtocol.id,
        editor_role: "user",
        content: editContent
      });
      
      if (response.status === "ok") {
        setEditMode(false);
        alert("Version saved successfully!");
        await loadAllData();
      }
    } catch (err) {
      setError("Failed to save version: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectProtocol = (protocol) => {
    setSelectedProtocol(protocol);
    setEditContent({
      title: protocol.title,
      purpose: protocol.purpose,
      steps: protocol.steps,
      notes: protocol.notes,
      references: protocol.references,
      roles: protocol.roles || [],
      department: protocol.department || null
    });
    setEditMode(false);
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      setError("Please select a file to upload");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await ingestFileUpload(
        uploadFile,
        uploadMetadata.title,
        uploadMetadata.roles,
        uploadMetadata.department
      );

      if (response.status === "ok") {
        setUploadDialogOpen(false);
        setUploadFile(null);  
        setUploadMetadata({ title: "", roles: "", department: "" });
        await loadAllData(); // Refresh all data including suggestions
        alert(`Document "${response.filename}" added to knowledge base!`);
      }
    } catch (err) {
      setError("Failed to upload document: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadFile(file);
      // Auto-populate title from filename if empty
      if (!uploadMetadata.title) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        const formattedTitle = nameWithoutExt.replace(/[_-]/g, " ").replace(/\b\w/g, l => l.toUpperCase());
        setUploadMetadata(prev => ({ ...prev, title: formattedTitle }));
      }
    }
  };

  const handleGenerateFromSuggestion = async (suggestion) => {
    try {
      setLoading(true);
      setError(null);
      
      // Use suggestion data to create a more targeted search
      const searchTerm = suggestion.topic || suggestion.title;
      const searchResponse = await searchProtocols(searchTerm);
      const chunks = searchResponse.results?.map(r => r.chunk_text || r.title) || [searchTerm];
      
      const response = await generateProtocol({
        title: suggestion.title,
        chunks: chunks,
        roles: suggestion.suggested_roles || [],
        department: suggestion.department || suggestion.departments?.[0] || null
      });
      
      if (response.status === "ok") {
        await loadAllData(); // Refresh all data
        setActiveTab("protocols"); // Switch to protocols tab
        alert(`Protocol "${suggestion.title}" generated successfully!`);
      }
    } catch (err) {
      setError("Failed to generate protocol: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = async (node) => {
    setSelectedNode(node);
    
    if (node.type === 'document') {
      // Fetch full document with content
      const documentId = node.id.replace('doc_', '');
      try {
        setLoading(true);
        const fullDoc = await fetchDocument(documentId);
        setSelectedDocument(fullDoc);
        setSelectedProtocol(null);
        setEditingDocumentMode(false);
        setEditDocumentData({
          title: fullDoc.title,
          roles: fullDoc.roles || [],
          department: fullDoc.department || null
        });
      } catch (err) {
        setError("Failed to load document: " + err.message);
      } finally {
        setLoading(false);
      }
    } else if (node.type === 'protocol') {
      // Find and select the protocol  
      const protocol = protocols.find(p => p.id === node.id.replace('protocol_', ''));
      if (protocol) {
        setSelectedProtocol(protocol);
        setSelectedDocument(null);
        setEditContent({
          title: protocol.title,
          purpose: protocol.purpose,
          steps: protocol.steps,
          notes: protocol.notes,
          references: protocol.references,
          roles: protocol.roles || [],
          department: protocol.department || null
        });
      }
    }
  };

  const handleNodeHover = (node) => {
    // Could show tooltip or highlight related nodes
    console.log('Hovering over:', node.title);
  };

  const handleEditDocument = (document) => {
    setEditingDocumentId(document.id);
    setEditDocumentTitle(document.title);
  };

  const handleSaveDocumentTitle = async () => {
    if (!editingDocumentId || !editDocumentTitle.trim()) return;

    try {
      setLoading(true);
      setError(null);
      
      await updateDocument(editingDocumentId, { title: editDocumentTitle.trim() });
      
      // Update local state
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === editingDocumentId 
            ? { ...doc, title: editDocumentTitle.trim() }
            : doc
        )
      );
      
      // Update selected document if it's the one being edited
      if (selectedDocument?.id === editingDocumentId) {
        setSelectedDocument(prev => ({ ...prev, title: editDocumentTitle.trim() }));
      }
      
      setEditingDocumentId(null);
      setEditDocumentTitle("");
      
    } catch (err) {
      setError("Failed to update document title: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditDocument = () => {
    setEditingDocumentId(null);
    setEditDocumentTitle("");
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme={false} />
      <Box
        sx={{
          height: "100vh",
          width: "100vw",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: 3,
          p: 3,
          boxSizing: "border-box",
        }}
      >
        {/* Top Left Panel - Knowledge Base Tabs */}
        <Paper
          sx={{
            gridRow: "1 / 2",
            gridColumn: "1 / 2",
            display: "flex",
            flexDirection: "column",
            p: 3,
            overflow: "hidden",
            color: "text.primary",
          }}
        >
          {/* Tab Navigation */}
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Button 
              variant={activeTab === "knowledge" ? "contained" : "outlined"}
              size="small"
              onClick={() => setActiveTab("knowledge")}
              startIcon={<Description />}
            >
              Knowledge ({documents.length})
            </Button>
            <Button 
              variant={activeTab === "protocols" ? "contained" : "outlined"}
              size="small"
              onClick={() => setActiveTab("protocols")}
              startIcon={<Assignment />}
            >
              Protocols ({protocols.length})
            </Button>
            <Button 
              variant={activeTab === "suggestions" ? "contained" : "outlined"}
              size="small"
              onClick={() => setActiveTab("suggestions")}
              color="secondary"
              startIcon={<Lightbulb />}
            >
              Suggestions ({suggestions.length})
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Upload Button - Always visible */}
          <Button
            variant="outlined"
            startIcon={<CloudUpload />}
            onClick={() => setUploadDialogOpen(true)}
            disabled={loading}
            sx={{ mb: 2 }}
            fullWidth
          >
            Upload Document to Knowledge Base
          </Button>

          {/* Loading */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Knowledge Base Tab */}
          {activeTab === "knowledge" && (
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                Ingested Documents
              </Typography>
              <List sx={{ flexGrow: 1, overflowY: "auto" }}>
                {documents.map((doc) => (
                  <ListItem key={doc.id} disablePadding>
                    {editingDocumentId === doc.id ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", p: 1 }}>
                        <TextField
                          value={editDocumentTitle}
                          onChange={(e) => setEditDocumentTitle(e.target.value)}
                          variant="outlined"
                          size="small"
                          fullWidth
                          onKeyPress={(e) => e.key === "Enter" && handleSaveDocumentTitle()}
                          sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
                        />
                        <IconButton 
                          onClick={handleSaveDocumentTitle}
                          disabled={loading || !editDocumentTitle.trim()}
                          color="primary"
                          size="small"
                        >
                          <Save />
                        </IconButton>
                        <IconButton 
                          onClick={handleCancelEditDocument}
                          size="small"
                        >
                          <Cancel />
                        </IconButton>
                      </Box>
                    ) : (
                      <ListItemButton 
                        onClick={async () => {
                          try {
                            setLoading(true);
                            const fullDoc = await fetchDocument(doc.id);
                            setSelectedDocument(fullDoc);
                            setSelectedProtocol(null);
                            setEditingDocumentMode(false);
                            setEditDocumentData({
                              title: fullDoc.title,
                              roles: fullDoc.roles || [],
                              department: fullDoc.department || null
                            });
                          } catch (err) {
                            setError("Failed to load document: " + err.message);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        selected={selectedDocument?.id === doc.id}
                      >
                        <ListItemText
                          primary={doc.title}
                          secondary={`${doc.chunk_count} chunks • ${doc.department || "No dept"} • ${doc.processed}`}
                        />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
              </List>
              {documents.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.6, textAlign: "center", p: 2 }}>
                  No documents uploaded yet. Upload some documents to build your knowledge base!
                </Typography>
              )}
            </Box>
          )}

          {/* Protocols Tab */}
          {activeTab === "protocols" && (
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              {/* Enhanced Search */}
              <EnhancedSearch
                onSearch={handleSearch}
                loading={loading}
                departments={graphData?.stats?.departments || []}
                roles={graphData?.stats?.roles || []}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
              />

              <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                Generated Protocols
              </Typography>
              <List sx={{ flexGrow: 1, overflowY: "auto" }}>
                {filteredProtocols.map((protocol) => (
                  <ListItem key={protocol.id} disablePadding>
                    <ListItemButton 
                      onClick={() => selectProtocol(protocol)}
                      selected={selectedProtocol?.id === protocol.id}
                    >
                      <ListItemText
                        primary={protocol.title}
                        secondary={`v${protocol.version} • ${protocol.department || "No dept"}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {protocols.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.6, textAlign: "center", p: 2 }}>
                  No protocols generated yet. Check suggestions to create protocols from your knowledge base!
                </Typography>
              )}
            </Box>
          )}

          {/* Suggestions Tab */}
          {activeTab === "suggestions" && (
            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                AI-Suggested Protocols
              </Typography>
              <List sx={{ flexGrow: 1, overflowY: "auto" }}>
                {suggestions.map((suggestion, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton>
                      <ListItemText
                        primary={suggestion.title}
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              {suggestion.description}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.6 }}>
                              Confidence: {Math.round(suggestion.confidence * 100)}% • {suggestion.source_documents} docs
                            </Typography>
                          </Box>
                        }
                      />
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={() => handleGenerateFromSuggestion(suggestion)}
                        disabled={loading}
                        sx={{ ml: 1 }}
                      >
                        Generate
                      </Button>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {suggestions.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.6, textAlign: "center", p: 2 }}>
                  {documents.length < 2 
                    ? "Upload at least 2 documents to get AI suggestions"
                    : "Analyzing knowledge base for suggestions..."
                  }
                </Typography>
              )}
            </Box>
          )}

        </Paper>

        {/* Bottom Left Panel - Document/Protocol Viewer */}
        <Paper
          sx={{
            gridRow: "2 / 3",
            gridColumn: "1 / 2",
            display: "flex",
            flexDirection: "column",
            p: 3,
            overflow: "auto",
            color: "text.primary",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Visibility /> Content Viewer
          </Typography>
          
          {selectedDocument && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {selectedDocument.title}
                </Typography>
                <Button
                  size="small"
                  variant={editingDocumentMode ? "outlined" : "contained"}
                  startIcon={editingDocumentMode ? <Cancel /> : <Edit />}
                  onClick={() => setEditingDocumentMode(!editingDocumentMode)}
                >
                  {editingDocumentMode ? "Cancel" : "Edit"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={async () => {
                    if (window.confirm(`Are you sure you want to delete "${selectedDocument.title}"?\n\nThis will remove the document and all its chunks from the knowledge base.\n\nThis action cannot be undone.`)) {
                      try {
                        setLoading(true);
                        setError(null);
                        await deleteDocument(selectedDocument.id);
                        
                        // Remove from local state immediately
                        setDocuments(prev => prev.filter(d => d.id !== selectedDocument.id));
                        setSelectedDocument(null);
                        
                        // Refresh graph data
                        const graphRes = await fetchGraphData();
                        setGraphData(graphRes);
                        
                      } catch (err) {
                        setError("Failed to delete document: " + err.message);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  disabled={loading}
                >
                  Delete
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {editingDocumentMode ? (
                <Paper sx={{ p: 2, mb: 2, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
                    <Edit /> Document Editor
                  </Typography>
                  
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Title Editing */}
                    <TextField
                      fullWidth
                      label="Title"
                      value={editDocumentData.title || selectedDocument.title}
                      onChange={(e) => setEditDocumentData({...editDocumentData, title: e.target.value})}
                      size="small"
                      variant="outlined"
                    />
                    
                    <Divider />
                    
                    {/* Department Selection */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={editDocumentData.department || ""}
                        onChange={(e) => {
                          setEditDocumentData({
                            ...editDocumentData, 
                            department: e.target.value,
                            roles: [] // Reset roles when department changes
                          });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        {getDepartments().map(dept => (
                          <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {/* Role Selection - Only show if department is selected */}
                    {editDocumentData.department && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", opacity: 0.9 }}>
                          Target Roles in {editDocumentData.department}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Roles</InputLabel>
                          <Select
                            multiple
                            value={editDocumentData.roles || []}
                            onChange={(e) => setEditDocumentData({...editDocumentData, roles: e.target.value})}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={value} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {getRolesForDepartment(editDocumentData.department).map(role => (
                              <MenuItem key={role} value={role}>
                                <Checkbox checked={editDocumentData.roles?.includes(role) || false} />
                                <ListItemText primary={role} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                    
                    <Divider />
                    
                    {/* Save Button */}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Save />}
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const updates = {
                            roles: editDocumentData.roles || [],
                            department: editDocumentData.department || null
                          };
                          
                          // Only include title if it was changed
                          if (editDocumentData.title && editDocumentData.title !== selectedDocument.title) {
                            updates.title = editDocumentData.title.trim();
                          }
                          
                          const response = await updateDocument(selectedDocument.id, updates);
                          
                          // Update the selected document immediately
                          if (response.status === "ok" && response.document) {
                            const updatedDoc = {
                              ...selectedDocument,
                              title: response.document.title || selectedDocument.title,
                              roles: response.document.roles,
                              department: response.document.department
                            };
                            setSelectedDocument(updatedDoc);
                            
                            // Update documents list
                            setDocuments(prev => 
                              prev.map(d => 
                                d.id === selectedDocument.id 
                                  ? { 
                                      ...d, 
                                      title: response.document.title || d.title,
                                      roles: response.document.roles, 
                                      department: response.document.department 
                                    }
                                  : d
                              )
                            );
                          }
                          
                          setEditingDocumentMode(false);
                          setError(null);
                        } catch (err) {
                          setError("Failed to update document: " + err.message);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      fullWidth
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Paper>
              ) : null}
              
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 2, maxHeight: "300px", overflowY: "auto", p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: 1 }}>
                {selectedDocument.content || "No content available"}
              </Typography>
              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {selectedDocument.roles?.map(role => (
                  <Chip key={role} label={role} size="small" color="primary" variant="outlined" />
                ))}
                {selectedDocument.department && (
                  <Chip label={selectedDocument.department} size="small" color="secondary" variant="outlined" />
                )}
              </Box>
            </Box>
          )}

          {selectedProtocol && (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {selectedProtocol.title}
                </Typography>
                <Button
                  size="small"
                  variant={editMode ? "outlined" : "contained"}
                  startIcon={editMode ? <Cancel /> : <Edit />}
                  onClick={() => setEditMode(!editMode)}
                >
                  {editMode ? "Cancel" : "Edit Protocol"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={async () => {
                    if (window.confirm(`Are you sure you want to delete "${selectedProtocol.title}"?\n\nThis action cannot be undone.`)) {
                      try {
                        setLoading(true);
                        setError(null);
                        await deleteProtocol(selectedProtocol.id);
                        
                        // Remove from local state immediately
                        setProtocols(prev => prev.filter(p => p.id !== selectedProtocol.id));
                        setFilteredProtocols(prev => prev.filter(p => p.id !== selectedProtocol.id));
                        setSelectedProtocol(null);
                        setEditMode(false);
                        
                        // Refresh graph data
                        const graphRes = await fetchGraphData();
                        setGraphData(graphRes);
                        
                      } catch (err) {
                        setError("Failed to delete protocol: " + err.message);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  disabled={loading}
                >
                  Delete
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {editMode ? (
                <Paper sx={{ p: 2, mb: 2, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
                    <Edit /> Protocol Editor
                  </Typography>
                  
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {/* Department Selection */}
                    <FormControl fullWidth size="small">
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={editContent.department || ""}
                        onChange={(e) => {
                          setEditContent({
                            ...editContent, 
                            department: e.target.value,
                            roles: [] // Reset roles when department changes
                          });
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        {getDepartments().map(dept => (
                          <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    {/* Role Selection - Only show if department is selected */}
                    {editContent.department && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", opacity: 0.9 }}>
                          Target Roles in {editContent.department}
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Roles</InputLabel>
                          <Select
                            multiple
                            value={editContent.roles || []}
                            onChange={(e) => setEditContent({...editContent, roles: e.target.value})}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={value} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            {getRolesForDepartment(editContent.department).map(role => (
                              <MenuItem key={role} value={role}>
                                <Checkbox checked={editContent.roles?.includes(role) || false} />
                                <ListItemText primary={role} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                    
                    <Divider />
                    
                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Save />}
                        onClick={async () => {
                          try {
                            setLoading(true);
                            const response = await updateProtocol(selectedProtocol.id, {
                              roles: editContent.roles || [],
                              department: editContent.department || null
                            });
                            
                            // Update the selected protocol immediately with the response
                            if (response.status === "ok" && response.protocol) {
                              const updatedProtocol = {
                                ...selectedProtocol,
                                roles: response.protocol.roles,
                                department: response.protocol.department
                              };
                              setSelectedProtocol(updatedProtocol);
                              
                              // Update protocols list
                              setProtocols(prev => 
                                prev.map(p => 
                                  p.id === selectedProtocol.id 
                                    ? updatedProtocol
                                    : p
                                )
                              );
                              setFilteredProtocols(prev => 
                                prev.map(p => 
                                  p.id === selectedProtocol.id 
                                    ? updatedProtocol
                                    : p
                                )
                              );
                            }
                            
                            setEditMode(false);
                            setError(null);
                          } catch (err) {
                            setError("Failed to update protocol: " + err.message);
                          } finally {
                            setLoading(false);
                          }
                        }}
                        disabled={loading}
                        fullWidth
                      >
                        Save Changes
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={async () => {
                          if (window.confirm(`Delete protocol "${selectedProtocol.title}"?`)) {
                            try {
                              setLoading(true);
                              await deleteProtocol(selectedProtocol.id);
                              await loadAllData();
                              setSelectedProtocol(null);
                              setEditMode(false);
                            } catch (err) {
                              setError("Failed to delete protocol: " + err.message);
                            } finally {
                              setLoading(false);
                            }
                          }
                        }}
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ) : null}
              
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
                Purpose
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedProtocol.purpose}
              </Typography>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Steps
              </Typography>
              <List dense>
                {selectedProtocol.steps?.map((step, index) => (
                  <ListItem key={index}>
                    <ListItemText 
                      primary={`${index + 1}. ${step}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {selectedProtocol.roles?.map(role => (
                  <Chip key={role} label={role} size="small" color="primary" variant="outlined" />
                ))}
                {selectedProtocol.department && (
                  <Chip label={selectedProtocol.department} size="small" color="secondary" variant="outlined" />
                )}
              </Box>
            </Box>
          )}

          {!selectedDocument && !selectedProtocol && (
            <Box sx={{ 
              display: "flex", 
              flexDirection: "column",
              alignItems: "center", 
              justifyContent: "center",
              height: "100%",
              opacity: 0.6,
              gap: 2
            }}>
              <Visibility sx={{ fontSize: 48 }} />
              <Typography variant="body1">
                Welcome to Loom
              </Typography>
              <Typography variant="body2" sx={{ textAlign: "center", maxWidth: 400 }}>
                Upload documents to build your knowledge base, then let AI suggest and generate protocols based on your content.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Right Panel - Knowledge Graph (Full Height) */}
        <Paper
          sx={{
            gridRow: "1 / 3",
            gridColumn: "2 / 3",
            display: "flex",
            flexDirection: "column",
            p: 3,
            overflow: "hidden",
            color: "text.primary",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <AccountTree /> Interactive Knowledge Graph
          </Typography>
          <Typography variant="caption" sx={{ mb: 2, opacity: 0.7 }}>
            {graphData?.stats?.total_nodes || 0} nodes • {graphData?.stats?.total_edges || 0} connections
          </Typography>
          
          <Box sx={{ 
            flexGrow: 1, 
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
          }}>
            {graphData ? (
              <KnowledgeGraph
                graphData={graphData}
                onNodeClick={handleNodeClick}
                onNodeHover={handleNodeHover}
                selectedNodeId={selectedNode?.id}
                width="100%"
                height="100%"
              />
            ) : (
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                height: "100%",
                flexDirection: "column",
                gap: 2
              }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  Building knowledge graph...
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.5 }}>
                  Documents: {documents.length}, Protocols: {protocols.length}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

      </Box>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.08)",
          }
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Upload Protocol Document
          <IconButton onClick={() => setUploadDialogOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* File Input */}
            <Box>
              <input
                type="file"
                accept=".txt,.md"
                onChange={handleFileSelect}
                style={{ display: "none" }}
                id="file-upload-input"
              />
              <label htmlFor="file-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  fullWidth
                  sx={{ py: 2, mb: 1 }}
                >
                  {uploadFile ? uploadFile.name : "Choose File (.txt, .md)"}
                </Button>
              </label>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Supported formats: .txt, .md
              </Typography>
            </Box>

            {/* Metadata Fields */}
            <TextField
              fullWidth
              label="Title"
              value={uploadMetadata.title}
              onChange={(e) => setUploadMetadata(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Auto-generated from filename"
              sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
            />
            
            <FormControl fullWidth size="small">
              <InputLabel>Department</InputLabel>
              <Select
                value={uploadMetadata.department}
                onChange={(e) => setUploadMetadata(prev => ({ 
                  ...prev, 
                  department: e.target.value,
                  roles: "" // Reset roles when department changes
                }))}
                sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
              >
                <MenuItem value="">None</MenuItem>
                {getDepartments().map(dept => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {uploadMetadata.department && (
              <FormControl fullWidth size="small">
                <InputLabel>Roles</InputLabel>
                <Select
                  multiple
                  value={uploadMetadata.roles ? uploadMetadata.roles.split(',').map(r => r.trim()) : []}
                  onChange={(e) => setUploadMetadata(prev => ({ 
                    ...prev, 
                    roles: e.target.value.join(', ')
                  }))}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
                >
                  {getRolesForDepartment(uploadMetadata.department).map(role => (
                    <MenuItem key={role} value={role}>
                      <Checkbox checked={uploadMetadata.roles.includes(role)} />
                      <ListItemText primary={role} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleFileUpload}
            variant="contained"
            disabled={!uploadFile || loading}
          >
            {loading ? <CircularProgress size={20} /> : "Upload & Ingest"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}
